import {
  activeReviews,
  dayKey,
  lemmaKey,
  type Snapshot,
  type Mode,
  type Direction,
  type ReviewEvent,
} from "./domain";
export function directionFor(mode: Mode): Direction {
  return mode === "collocation"
    ? "collocation"
    : ["produce", "cloze"].includes(mode)
      ? "productive"
      : "receptive";
}
export function difficultWords(s: Snapshot) {
  const events = activeReviews(s.events),
    result = new Set(s.words.filter((w) => w.difficult && !w.deleted).map((w) => w.id));
  const grouped = new Map<string, ReviewEvent[]>();
  const activeIds = new Set(
    s.words.filter((w) => w.active && !w.deleted).map((w) => w.id),
  );
  for (const e of events) {
    const list = grouped.get(e.wordId) ?? [];
    list.push(e);
    grouped.set(e.wordId, list);
  }
  for (const [wordId, list] of grouped) {
    if (!activeIds.has(wordId)) continue;
    const recent = list.sort((a, b) => a.at.localeCompare(b.at)).slice(-5);
    if (
      recent.filter((e) => e.effectiveRating === 1).length >= 2 ||
      recent.filter((e) => e.hint).length >= 3
    )
      result.add(wordId);
  }
  return result;
}
export function buildQueue(
  s: Snapshot,
  mode: Mode,
  kind: "daily" | "due" | "difficult" = "daily",
  now = new Date(),
) {
  if (mode === "collocation" && !s.settings.collocationTests) return [];
  const events = activeReviews(s.events),
    direction = directionFor(mode),
    wordMap = new Map(
      s.words.filter((w) => w.active && !w.deleted).map((w) => [w.id, w]),
    );
  const stateMap = new Map(s.states.map((v) => [v.cardId, v]));
  const hasExample = new Set(s.examples.map((e) => e.wordId));
  const hard = difficultWords(s),
    introduced = new Set(events.map((e) => e.wordId));
  const firstDate = new Map<string, string>();
  const allWords = new Map(s.words.map((w) => [w.id, w]));
  for (const e of events) {
    const l = allWords.get(e.wordId);
    if (l) {
      const key = lemmaKey(l),
        date = dayKey(e.at);
      if (!firstDate.has(key) || date < firstDate.get(key)!) firstDate.set(key, date);
    }
  }
  const today = dayKey(now),
    learnedToday = [...firstDate.values()].filter((v) => v === today).length;
  let remaining = Math.max(0, s.settings.dailyNew - learnedToday);
  const newLemmas = new Set<string>(),
    selected: string[] = [];
  const eligible = s.cards.filter(
    (c) =>
      c.direction === direction &&
      wordMap.has(c.wordId) &&
      stateMap.has(c.id) &&
      hasExample.has(c.wordId) &&
      (mode !== "collocation" || !!wordMap.get(c.wordId)?.collocations.length),
  );
  const due = eligible.filter((c) => {
    const st = stateMap.get(c.id)!;
    return st.memory.reps > 0 && Date.parse(st.memory.dueAt) <= now.getTime();
  });
  due.sort((a, b) => {
    const x = stateMap.get(a.id)!,
      y = stateMap.get(b.id)!;
    return (
      x.memory.dueAt.localeCompare(y.memory.dueAt) ||
      (s.settings.difficultFirst
        ? Number(hard.has(b.wordId)) - Number(hard.has(a.wordId))
        : 0)
    );
  });
  if (kind === "difficult")
    return eligible
      .filter((c) => hard.has(c.wordId))
      .sort((a, b) =>
        stateMap
          .get(a.id)!
          .memory.dueAt.localeCompare(stateMap.get(b.id)!.memory.dueAt),
      )
      .slice(0, 30)
      .map((c) => c.id);
  selected.push(...due.map((c) => c.id));
  const buckets = new Map<string, typeof eligible>();
  for (const c of eligible.filter((c) => stateMap.get(c.id)!.memory.reps === 0)) {
    const category = wordMap.get(c.wordId)!.pos;
    const bucket = buckets.get(category) ?? [];
    bucket.push(c);
    buckets.set(category, bucket);
  }
  const mixedNew: typeof eligible = [];
  const categoryLists = [...buckets.values()];
  for (let row = 0; categoryLists.some((b) => row < b.length); row++)
    for (const b of categoryLists) {
      const c = b[row];
      if (c) mixedNew.push(c);
    }
  if (kind !== "due")
    for (const c of mixedNew) {
      const lemma = lemmaKey(wordMap.get(c.wordId)!);
      if (introduced.has(c.wordId) || newLemmas.has(lemma) || firstDate.has(lemma)) {
        selected.push(c.id);
        continue;
      }
      if (remaining > 0) {
        remaining--;
        newLemmas.add(lemma);
        selected.push(c.id);
      }
    }
  return selected.slice(0, 60);
}
